import React from 'react';
import { observer } from 'mobx-react';
import {
  FormGroup,
  FormControl,
  ControlLabel,
  Button,
  Alert,
} from 'react-bootstrap';
import {
  Formik,
  Form,
} from 'formik';
import * as yup from 'yup';
import cx from 'classnames';
import { useTranslationWithFallback } from '../../hooks/use-translation-with-fallback';
import { MaterialType } from '../../constants/MaterialModels';
import { useStores } from '../../hooks/use-stores';
import { Material } from '../../../types/api/Material';
import { Device } from '../../../types/api/Device';
import SideBar from '../SideBar/SideBar';
import logger from '../../utils/logger';

interface FormValues {
  substituteMaterialId: string;
  reason: string;
}

interface MaterialSubstitutionData {
  originalMaterialId?: string | number;
  scaleId?: string | number;
  currentWeight?: number;
}

// TODO: This should be removed once MaterialStore is fully typed
interface BinAssignment {
  isActive: boolean;
  binId: string | number;
}

const MaterialSubstitutionSidebar: React.FC = () => {
  const t = useTranslationWithFallback();
  const { batchStore, materialStore, devicesStore, sidebarStore } = useStores();

  const validationSchema = yup.object().shape({
    substituteMaterialId: yup.string().required(t('materialSubstitution.validation.selectMaterial', 'Please select a substitute material')),
    reason: yup.string().required(t('materialSubstitution.validation.provideReason', 'Please provide a reason for the substitution')),
  });

  const initialValues: FormValues = {
    substituteMaterialId: '',
    reason: '',
  };

  const handleSubmit = async (values: FormValues, { setSubmitting }: any) => {
    const { originalMaterialId, scaleId, currentWeight } = batchStore.materialSubstitutionData as unknown as MaterialSubstitutionData;

    try {
      const success = batchStore.changeMaterialMidBatch({
        originalMaterialId: String(originalMaterialId),
        substituteMaterialId: values.substituteMaterialId,
        scaleId: String(scaleId),
        reason: values.reason,
        currentWeight: currentWeight || 0,
      });

      if (success) {
        sidebarStore.onToggle(null);
      }
    } catch (error) {
      logger.error('Material substitution failed:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    sidebarStore.onToggle(null);
  };

  const getCompatibleMaterials = (originalMaterialId: string | number, scaleId: string | number): Material[] => {
    const originalMaterial = materialStore.getMaterialByItemId(originalMaterialId);
    if (!originalMaterial) return [];

    return materialStore.assignedMaterials.filter((material: Material) => {
      if (material.assignment !== originalMaterial.assignment ||
          material.itemId === originalMaterialId ||
          !material.visible) {
        return false;
      }

      // Check if material has bins assigned to the same scale
      // @ts-ignore TODO: Remove this once MaterialStore is fully typed
      const materialDetails = materialStore.materialDetails[material.materialDetailId];
      if (!materialDetails || !materialDetails.bins || !materialDetails.bins.length) {
        return false;
      }

      // Find if any active bins are assigned to the target scale
      return materialDetails.bins.some((binAssignment: BinAssignment) => {
        if (!binAssignment.isActive) {
          return false;
        }

        const bin = devicesStore.getDeviceById(binAssignment.binId);
        return bin && String(bin.scaleId) === String(scaleId);
      });
    });
  };

  const { originalMaterialId, scaleId, currentWeight } = batchStore.materialSubstitutionData as unknown as MaterialSubstitutionData;

  // Don't render if no data is provided
  if (!originalMaterialId || !scaleId) {
    return (
      <SideBar type="materialSubstitution" title={t('materialSubstitution.title', 'Material Substitution')} icon="fa-exchange">
        <Alert bsStyle="warning">
          {t('materialSubstitution.noDataAvailable', 'No material substitution data available.')}
        </Alert>
      </SideBar>
    );
  }

  const originalMaterial = materialStore.getMaterialByItemId(originalMaterialId);
  const scale: Device = devicesStore.getDeviceById(scaleId);
  const compatibleMaterials = getCompatibleMaterials(originalMaterialId, scaleId);

  return (
    <SideBar type="materialSubstitution" title={t('materialSubstitution.title', 'Material Substitution')} icon="fa-exchange">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values, errors, touched, isSubmitting }) => {
          const selectedMaterial = values.substituteMaterialId
            ? materialStore.getMaterialByItemId(values.substituteMaterialId)
            : null;

          const handleMaterialChange = (e: any) => {
            setFieldValue('substituteMaterialId', e.target.value);
          };

          return (
            <Form>
              <div className="alert alert-info">
                <strong>{t('materialSubstitution.currentBatchInfo')}:</strong>
                <br />
                {t('materialSubstitution.scale')}: {scale?.description || t('materialSubstitution.unknown')}
                <br />
                {t('materialSubstitution.originalMaterial')}: {materialStore.getMaterialName(originalMaterial)}
                <br />
                {t('materialSubstitution.currentWeight')}: {currentWeight} {scale?.unitId ? batchStore.getUnitById(scale?.unitId) : t('materialSubstitution.units')}
              </div>

              <FormGroup className={cx({ 'has-error': errors.substituteMaterialId && touched.substituteMaterialId })}>
                <ControlLabel>{t('materialSubstitution.selectSubstituteMaterial')} *</ControlLabel>
                <FormControl
                  componentClass="select"
                  name="substituteMaterialId"
                  value={values.substituteMaterialId}
                  onChange={handleMaterialChange}
                  disabled={isSubmitting}
                >
                  <option value="">-- {t('materialSubstitution.selectMaterial')} --</option>
                  {compatibleMaterials.map((material: Material) => {
                    return (
                      <option
                        key={material.itemId}
                        value={material.itemId}
                      >
                        {materialStore.getMaterialName(material)} ({material.assignment})
                      </option>
                    );
                  })}
                </FormControl>
                {errors.substituteMaterialId && touched.substituteMaterialId && (
                  <span className="help-block">{errors.substituteMaterialId}</span>
                )}
              </FormGroup>

              {selectedMaterial && (
                <div className="alert alert-success">
                  <strong>{t('materialSubstitution.substituteMaterialDetails')}:</strong>
                  <br />
                  {t('materialSubstitution.code')}: {selectedMaterial.code}
                  <br />
                  {t('materialSubstitution.assignment')}: {selectedMaterial.assignment}
                  <br />
                  {selectedMaterial.assignment === MaterialType.Weighed && (
                    <>{t('materialSubstitution.scale')}: {devicesStore.getDeviceById(selectedMaterial.scaleId)?.description || t('materialSubstitution.sameAsOriginal')}</>
                  )}
                  {selectedMaterial.assignment === MaterialType.Metered && (
                    <>{t('materialSubstitution.pump')}: {devicesStore.getDeviceById(selectedMaterial.pumpId)?.description || t('materialSubstitution.notAssigned')}</>
                  )}
                </div>
              )}

              <FormGroup className={cx({ 'has-error': errors.reason && touched.reason })}>
                <ControlLabel>{t('materialSubstitution.reasonForSubstitution', 'Reason for Substitution')} *</ControlLabel>
                <FormControl
                  componentClass="textarea"
                  rows={3}
                  name="reason"
                  placeholder={t('materialSubstitution.reasonPlaceholder', 'Enter reason for material substitution (e.g., \'Original material unavailable\', \'Quality issue\', etc.)')}
                  value={values.reason}
                  onChange={(e: any) => setFieldValue('reason', e.target.value)}
                  disabled={isSubmitting}
                />
                {errors.reason && touched.reason && (
                  <span className="help-block">{errors.reason}</span>
                )}
              </FormGroup>

              <div className="alert alert-warning">
                <strong>{t('materialSubstitution.important', 'Important')}:</strong>
                <ul className="mb-0">
                  <li>{t('materialSubstitution.warnings.updateInventory', 'This will update inventory records for both materials')}</li>
                  <li>{t('materialSubstitution.warnings.debitOriginal', 'The original material will be debited for the current weight')}</li>
                  <li>{t('materialSubstitution.warnings.useSubstitute', 'The substitute material will be used for remaining batching')}</li>
                  <li>{t('materialSubstitution.warnings.updateRecords', 'All batch records will reflect the substitution')}</li>
                  <li>{t('materialSubstitution.warnings.cannotUndo', 'This action cannot be undone')}</li>
                </ul>
              </div>

              <div className="text-right" style={{ marginTop: '20px' }}>
                <Button onClick={handleCancel} disabled={isSubmitting} style={{ marginRight: '10px' }}>
                  {t('materialSubstitution.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  bsStyle="primary"
                  disabled={isSubmitting || !values.substituteMaterialId}
                >
                  {isSubmitting ? t('materialSubstitution.processing', 'Processing...') : t('materialSubstitution.substituteMaterial', 'Substitute Material')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </SideBar>
  );
};

export default observer(MaterialSubstitutionSidebar);
